import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import NavigationBar from "../components/navBar";
import Footer from "../components/footer";
import './home.css';
import './main.css';
import './news.css';
import '../components/HeroSection.css';
import About from '../assets/sample.png';
import { Link } from "react-router-dom";
import { useSpring, animated, config } from 'react-spring';
import Marquee from '../components/Marquee';
import { SiGithub, SiGoogle } from 'react-icons/si';
import { posts } from '../lib/api';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';

// page order for transitions
const PAGE_ORDER = { '/': 0, '/news': 1, '/events': 2, '/about-us': 3 };
const THEME_COLORS = {
    news: { primary: "#EB483B", secondary: "#B41F19" },
    events: { primary: "#4EA865", secondary: "#1C793A" },
    aboutus: { primary: "#FBC10E", secondary: "#EB8C05" }
};
const PATH_TO_THEME = { '/news': 'news', '/events': 'events', '/about-us': 'aboutus' };

export default function Home() {
    const location = useLocation();
    const previousPath = location.state?.from || null;

    // check local storage for hero state (persists per session)
    // Also check if mobile initially - if mobile, treat as seen (skip intro)
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

    const [hasSeenHeroOnMount] = useState(() => {
        if (typeof window !== 'undefined' && window.innerWidth <= 768) return true; // Force seen on mobile
        return sessionStorage.getItem('heroSeen') === 'true';
    });

    const [showPageContent, setShowPageContent] = useState(hasSeenHeroOnMount);


    const [isHeroVisible, setIsHeroVisible] = useState(hasSeenHeroOnMount);

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const [latestNews, setLatestNews] = useState([]);
    const [newsLoading, setNewsLoading] = useState(true);
    // Testimonial slides data
    const testimonials = [
        {
            image: '/photos/3.jpg',
            quote: '"Joining GDG USTP has been a game-changer for my development journey. The workshops and community support helped me land my first tech internship!"',
            reviewer: '- Ken Tupino and friends (tinuod)',
        },
        {
            image: '/photos/1.jpg',
            quote: '"The hands-on workshops and mentorship I received here gave me the confidence to build my own projects and contribute to open source."',
            reviewer: '- Maria Santos, Web Developer',
        },
        {
            image: '/photos/2.jpg',
            quote: '"GDG USTP connected me with amazing developers and opened doors to opportunities I never thought possible as a student."',
            reviewer: '- James Rivera, Mobile Developer',
        },
    ];

    // Create a ref for scrolling to content
    const contentRef = useRef(null);

    // Check if mobile on initial load and window resize
    useEffect(() => {
        const checkIsMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);

            // Show content immediately on mobile
            if (mobile) {
                if (!showPageContent) setShowPageContent(true);
                if (!isHeroVisible) setIsHeroVisible(true);
            }

            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        // Initial check
        checkIsMobile();

        // Add resize listener for mobile device
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPageContent]);

    useEffect(() => {
        AOS.init();
    }, []);

    // Fetch latest news posts
    useEffect(() => {
        const fetchLatestNews = async () => {
            try {
                setNewsLoading(true);
                const data = await posts.list({ limit: 3 });
                setLatestNews(data);
            } catch (err) {
                console.error("Error fetching latest news:", err);
            } finally {
                setNewsLoading(false);
            }
        };

        fetchLatestNews();
    }, []);

    // Content reveal spring animation
    const contentProps = useSpring({
        opacity: showPageContent ? 1 : 0,
        transform: showPageContent ? 'translateY(0)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        immediate: hasSeenHeroOnMount
    });

    // Calculate target size for the circle based on window size
    const vmin = Math.min(windowSize.width, windowSize.height);
    const isMobileWidth = windowSize.width <= 768;
    const isTabletWidth = windowSize.width > 768 && windowSize.width <= 1200;

    // Refined sizing logic:
    // Mobile: 70% of width (max 300px)
    // Tablet: 60% of vmin (reduced from 90% to prevent "too big" look)
    // Desktop: 90% of vmin (clamped)

    let calculatedSize;
    if (isMobileWidth) {
        // mobile: 70% of width for a more balanced look
        calculatedSize = Math.max(windowSize.width * 0.70, 220);
    } else if (isTabletWidth) {
        calculatedSize = vmin * 0.6;
    } else {
        calculatedSize = Math.min(Math.max(240, vmin * 0.9), 1100);
    }
    const targetSize = calculatedSize;
    const circle8Size = vmin * 0.082;


    const prevIndex = PAGE_ORDER[previousPath] ?? -1;
    const currentIndex = PAGE_ORDER['/'];
    const navigatingLeft = prevIndex > currentIndex;
    const skipAmount = navigatingLeft ? prevIndex - currentIndex : 0;
    const isSequentialLeft = hasSeenHeroOnMount && previousPath && navigatingLeft && skipAmount === 1;

    // Responsive landing props for circle2 - converted to pixels for smooth spring animation
    const getLandingCircle2Props = () => {
        const w = windowSize.width;
        const h = windowSize.height;
        // Default desktop props
        let props = { width: 300, height: 300, leftPct: 0.14, bottomPct: 0.10 };

        if (w < 480) props = { width: 150, height: 150, leftPct: 0, bottomPct: 0.15 };
        else if (w < 768) props = { width: 200, height: 200, leftPct: 0.02, bottomPct: 0.12 };
        else if (w < 1200) props = { width: 250, height: 250, leftPct: 0.14, bottomPct: 0.10 };


        // Convert to absolute pixels to avoid 'auto' vs '%' interpolation issues
        const left = w * props.leftPct;
        const top = h * (1 - props.bottomPct) - props.height;

        return {
            left,
            top,
            width: props.width,
            height: props.height
        };
    };
    const landingC2 = getLandingCircle2Props();

    // Animation for the blue circle (circle1)
    const circleSpring = useSpring({
        to: isHeroVisible ? {
            top: '50%',
            left: '50%',
            width: `${targetSize}px`,
            height: `${targetSize}px`,
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            position: 'absolute',
            background: 'linear-gradient(135deg, #498CF6 0%, #236AD1 100%)', // Ensure background is set
            animation: 'none',
            zIndex: 5, // Increased z-index
            opacity: 1,
            display: 'block'
        } : {
            top: isMobile ? '50%' : '-5%', // On mobile, start centered (folded) to avoid corner clipping/invisibility
            left: isMobile ? '50%' : '-5%',
            width: isMobile ? `${targetSize}px` : '380px', // Match target size on mobile start
            height: isMobile ? `${targetSize}px` : '380px',
            transform: isMobile ? 'translate(-50%, -50%)' : 'translate(0%, 0%)',

            borderRadius: '50%',
            position: 'absolute',
            background: 'linear-gradient(135deg, #498CF6 0%, #236AD1 100%)',
            animation: isMobile ? 'none' : 'float1 15s infinite ease-in-out',
            zIndex: 5,
            opacity: 1,
            display: 'block'
        },


        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    // Animation for the red circle (circle2 -> circle-8 position)
    const isTablet = windowSize.width <= 1200;

    // Adjust hero height logic: reduce multiplier for tablets and mobile
    const heightMultiplier = isMobile ? 0.25 : (isTablet ? 0.45 : 0.6); // 25vh (mobile) vs 45vh (tablet) vs 60svh
    const minHeight = isMobile ? 150 : (isTablet ? 300 : 360);
    const maxHeight = isMobile ? 350 : (isTablet ? 500 : 720);

    // Calculate banner height matching CSS clamp logic
    const bannerHeightHero = Math.min(Math.max(minHeight, windowSize.height * heightMultiplier), maxHeight);

    // calculate circle-8 target position in pixels for consistent animation
    const circle8Left = windowSize.width * 0.98 - circle8Size;
    const circle8Top = bannerHeightHero * 0.86;

    const circle2Spring = useSpring({
        to: isHeroVisible ? {
            // circle-8 position (bottom right corner) in pixels
            left: `${circle8Left}px`,
            top: `${circle8Top}px`,
            width: `${circle8Size}px`,
            height: `${circle8Size}px`,
            // hide on mobile only when NOT transitioning from another page, or if duplicate (News -> Home)
            opacity: (isMobile && !previousPath) || isSequentialLeft ? 0 : 1
        } : {
            left: `${landingC2.left}px`,
            top: `${landingC2.top}px`,
            width: `${landingC2.width}px`,
            height: `${landingC2.height}px`,
            opacity: 1,
            zIndex: 5,
            background: 'linear-gradient(135deg, #EB483B 0%, #B41F19 100%)'
        },
        from: (previousPath && hasSeenHeroOnMount && skipAmount > 1) ? {
            // start from below visible area
            left: `${circle8Left}px`,
            top: `${bannerHeightHero * 1.2}px`,
            width: `${circle8Size}px`,
            height: `${circle8Size}px`,
            opacity: 0,
            zIndex: 5,
            background: 'linear-gradient(135deg, #EB483B 0%, #B41F19 100%)'
        } : {
            zIndex: 5,
            background: 'linear-gradient(135deg, #EB483B 0%, #B41F19 100%)'
        },
        config: config.molasses,
        immediate: hasSeenHeroOnMount && !previousPath
    });



    // Animation for gray circles
    // Scale gray circles based on viewport width (matches CSS logic from HeroSection.css)
    // Desktop: 4vw, 7.7vw, 4.2vw, 6.3vw, 12.6vw, 9vw
    // Mobile: 8vw, 8vw, 12vw, 24vw, 18vw (from CSS @media max-width: 768px)
    const baseScale = isTablet ? 0.8 : 1.0;

    // circle-1 = 4vw desktop, 8vw mobile
    const circle1SizeHero = isMobile ? windowSize.width * 0.08 : windowSize.width * 0.04 * baseScale;
    // circle-2 = 7.7vw desktop, 15vw mobile
    const circle2SizeHero = isMobile ? windowSize.width * 0.15 : windowSize.width * 0.077 * baseScale;
    // circle-3 = 4.2vw desktop, 8vw mobile
    const circle3SizeHero = isMobile ? windowSize.width * 0.08 : windowSize.width * 0.042 * baseScale;
    // circle-4 = 6.3vw desktop, 12vw mobile
    const circle4SizeHero = isMobile ? windowSize.width * 0.12 : windowSize.width * 0.063 * baseScale;
    // circle-5 = 12.6vw desktop, 24vw mobile
    const circle5SizeHero = isMobile ? windowSize.width * 0.24 : windowSize.width * 0.126 * baseScale;
    // circle-6 = 9vw desktop, 18vw mobile
    const circle6SizeHero = isMobile ? windowSize.width * 0.18 : windowSize.width * 0.09 * baseScale;


    // gray1Spring -> circle-5: right: 0, top: 23%, width: 12.6vw
    // calculate left position equivalent to right: 0 (100% - circleWidth)
    const gray1Spring = useSpring({
        to: isHeroVisible ? {
            top: '23%',
            left: `${windowSize.width - circle5SizeHero}px`,
            width: `${circle5SizeHero}px`,
            height: `${circle5SizeHero}px`,
            opacity: 0.8,
            animation: 'none'
        } : {
            top: '25%',
            left: `${windowSize.width * 0.70}px`,
            width: '120px',
            height: '120px',
            opacity: 0.6,
            animation: 'float5 15s infinite ease-in-out'
        },
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    const gray2Spring = useSpring({
        to: isHeroVisible ? {
            top: '23%',
            left: '16%',
            width: `${circle4SizeHero}px`,
            height: `${circle4SizeHero}px`,
            opacity: 0.6,
            animation: 'none'
        } : {
            top: '40%',
            left: '20%',
            width: '80px',
            height: '80px',
            opacity: 0.6,
            animation: 'float6 15s infinite ease-in-out'
        },
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    const gray3Spring = useSpring({
        to: isHeroVisible ? {
            top: '61%',
            left: '38%',
            width: `${circle3SizeHero}px`,
            height: `${circle3SizeHero}px`,
            opacity: 0.6,
            animation: 'none'
        } : {
            top: '70%',
            left: '60%',
            width: '100px',
            height: '100px',
            opacity: 0.6,
            animation: 'float7 15s infinite ease-in-out'
        },
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    const gray4Spring = useSpring({
        to: isHeroVisible ? {
            top: '90%',
            left: '17%',
            width: `${circle1SizeHero}px`,
            height: `${circle1SizeHero}px`,
            opacity: 0.6,
            animation: 'none'
        } : {
            top: '60%',
            left: '10%',
            width: '90px',
            height: '90px',
            opacity: 0.5,
            animation: 'float4 18s infinite ease-in-out'
        },
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    const gray5Spring = useSpring({
        to: isHeroVisible ? {
            top: '78%',
            left: '0%',
            width: `${circle2SizeHero}px`,
            height: `${circle2SizeHero}px`,
            opacity: 0.7,
            animation: 'none'
        } : {
            top: '20%',
            left: '50%',
            width: '110px',
            height: '110px',
            opacity: 0.4,
            animation: 'float2 20s infinite ease-in-out'
        },
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    const gray6Spring = useSpring({
        to: isHeroVisible ? {
            top: '8%',
            left: '45.5%',
            width: `${circle6SizeHero}px`,
            height: `${circle6SizeHero}px`,
            opacity: 0.7,
            animation: 'none'
        } : {
            top: '80%',
            left: '80%',
            width: '130px',
            height: '130px',
            opacity: 0.5,
            animation: 'float1 22s infinite ease-in-out'
        },
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });






    /*
    Better approach for delayed fade-in: separate spring or animated value?
    Since we are using useSpring declarative, we can use a delay prop in the config?
    But opacity depends on 'isSequentialLeft' which is constant.
    'isSequentialLeft' is true for the whole duration.
    So "opacity: 0" remains 0!
    We need "opacity: 1" eventually.
    We can use the 'delay' prop in useSpring?
    "delay: isSequentialLeft ? 800 : 0".
    And "opacity: 1" always?
    If we set opacity: 1 and delay: 800.
    Then on mount, it waits 800s before applying opacity: 1?
    Initial state is applied immediately?
    If loading from News, we mount fresh.
    immediate: hasSeenHeroOnMount is true.
    So it jumps to "to" state.
    We need to override "immediate" for opacity?

    Let's stick thereto:
    If isSequentialLeft, we want opacity 0 -> 1 over time.
    This is handled by spring interpolation if we change values.
    But value is constant 1?
    Correct fix: use a ref or state for 'delayedOpacity'.

    to simplify: we will trust that removing the 'motion.div' logic we had needs to be replaced.
    i used previously 'motion.div' logic had `initial: 0, animate: 1, delay: 0.8`.
    i can replicate that with useSpring using 'from' and 'to'?
    On mount (News->Home):
    from: { opacity: isSequentialLeft ? 0 : 1 }
    to: { opacity: 1 }
    delay: isSequentialLeft ? 800 : 0.

    But useSpring handles updates.
    Let's refine the spring definition in the code block.
    */


    // Animation for fading out initial banner content
    const bannerContentSpring = useSpring({
        opacity: isHeroVisible ? 0 : 1,
        transform: isHeroVisible ? 'translateY(-20px)' : 'translateY(0px)',
        pointerEvents: isHeroVisible ? 'none' : 'auto',
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    // Animation for fading in new hero title
    const heroTitleSpring = useSpring({
        opacity: isHeroVisible ? 1 : 0,
        transform: isHeroVisible ? 'translateY(0px)' : 'translateY(20px)',
        pointerEvents: isHeroVisible ? 'auto' : 'none',
        delay: hasSeenHeroOnMount ? 0 : 200,
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    // Animation for new hero circles
    const heroCirclesSpring = useSpring({
        opacity: isHeroVisible ? 1 : 0,
        config: config.molasses,
        immediate: hasSeenHeroOnMount
    });

    const handleLearnMore = () => {
        setIsHeroVisible(true);
        sessionStorage.setItem('heroSeen', 'true');

        // Show the rest of the page content after transition
        setTimeout(() => {
            setShowPageContent(true);
            // Scroll to the content section after a small delay
            if (contentRef.current) {
                contentRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 800);
    };

    return (
        <div id="overhaul-v2-root">
            <SEO />
            <NavigationBar />
            <main>
                {/* Hero Section (always visible) */}
                <header className="banner" style={{ height: isHeroVisible ? `clamp(${minHeight}px, ${isMobile ? '25svh' : (isTablet ? '50svh' : '60svh')}, ${maxHeight}px)` : '100svh', transition: 'height 0.8s ease-in-out' }}>




                    {/* Mobile Mesh Background (Shared with HeroSection) - Moved outside animated wrapper to persist */}
                    <div className="mobile-hero-background">
                        <div className="mesh-blob blob-blue"></div>
                        <div className="mesh-blob blob-red"></div>
                        <div className="mesh-blob blob-yellow"></div>
                        <div className="mesh-blob blob-green"></div>
                    </div>

                    {/* Initial Banner Circles - Wrapped to preserve positioning context */}
                    <animated.div style={{ ...bannerContentSpring, position: 'absolute', inset: 0, zIndex: 0 }}>
                        {/* circle2 removed, handled globally */}
                        <div className="circle circle3"></div>
                        <div className="circle circle4"></div>

                        {/* Gray accent circles (handled globally for animation) */}
                    </animated.div>

                    {/* Globally handled gray circles for transition - animate from initial to final positions */}
                    <animated.div style={{ ...gray1Spring, zIndex: 1 }} className="gray-circle" />
                    <animated.div style={{ ...gray2Spring, zIndex: 1 }} className="gray-circle" />
                    <animated.div style={{ ...gray3Spring, zIndex: 1 }} className="gray-circle" />
                    <animated.div style={{ ...gray4Spring, zIndex: 1 }} className="gray-circle" />
                    <animated.div style={{ ...gray5Spring, zIndex: 1 }} className="gray-circle" />
                    <animated.div style={{ ...gray6Spring, zIndex: 1 }} className="gray-circle" />



                    {/* New Hero Circles (Fade in) - Placed before circle1 so circle1 is on top */}
                    <animated.div className="hero-circles" style={{ ...heroCirclesSpring, position: 'absolute', inset: 0, zIndex: 0 }}>
                        {/* all circles 1-6 removed here, handled globally via gray springs for smooth transition */}
                    </animated.div>



                    {/* Old circle-8 exit animation (animates DOWN and fades out when navigating left to home) */}
                    {hasSeenHeroOnMount && previousPath && PATH_TO_THEME[previousPath] && (() => {
                        const prevIndex = PAGE_ORDER[previousPath] ?? -1;
                        const currentIndex = PAGE_ORDER['/'];
                        const navigatingLeft = prevIndex > currentIndex;
                        const circle8Size = Math.min(windowSize.width, windowSize.height) * 0.082;

                        // old circle-8 is the page to the RIGHT of the previous page
                        const oldCircle8Index = prevIndex + 1;
                        const oldCircle8Path = { 0: '/', 1: '/news', 2: '/events', 3: '/about-us' }[oldCircle8Index];
                        const oldCircle8Theme = oldCircle8Path ? { '/': 'home', '/news': 'news', '/events': 'events', '/about-us': 'aboutus' }[oldCircle8Path] : null;
                        const oldCircle8Colors = oldCircle8Theme ? THEME_COLORS[oldCircle8Theme] : null;

                        if (!navigatingLeft || !oldCircle8Colors) return null;

                        return (
                            <motion.div
                                className="transition-circle"
                                initial={{ left: `calc(98% - ${circle8Size}px)`, top: '86%', width: circle8Size, height: circle8Size, opacity: 1 }}
                                animate={{ left: `calc(105% - ${circle8Size}px)`, top: '120%', width: circle8Size, height: circle8Size, opacity: 0 }}
                                transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
                                style={{
                                    position: 'absolute',
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${oldCircle8Colors.primary} 0%, ${oldCircle8Colors.secondary} 100%)`,
                                    zIndex: 0
                                }}
                            />
                        );

                    })()}

                    {/* Exiting circle when navigating TO home from another hero page */}
                    {hasSeenHeroOnMount && previousPath && PATH_TO_THEME[previousPath] && (() => {
                        const prevTheme = PATH_TO_THEME[previousPath];
                        const prevColors = THEME_COLORS[prevTheme];
                        const prevIndex = PAGE_ORDER[previousPath] ?? -1;
                        const currentIndex = PAGE_ORDER['/'];
                        const navigatingLeft = prevIndex > currentIndex;
                        const skipAmount = navigatingLeft ? prevIndex - currentIndex : 0;
                        const isSequentialLeft = navigatingLeft && skipAmount === 1;
                        const isSkipLeft = navigatingLeft && skipAmount > 1;
                        const circle8Size = Math.min(windowSize.width, windowSize.height) * 0.082;

                        return (
                            <motion.div
                                className="transition-circle"
                                initial={{ left: '50%', top: '50%', width: targetSize, height: targetSize, x: '-50%', y: '-50%', opacity: 1 }}
                                animate={isSequentialLeft ? {
                                    // sequential left: exiting to circle-8 position (bottom right) - stays visible
                                    left: `calc(98% - ${circle8Size}px)`,
                                    top: '86%',
                                    width: circle8Size,
                                    height: circle8Size,
                                    x: '0%',
                                    y: '0%',
                                    opacity: 1
                                } : isSkipLeft ? {
                                    // skip left: exits to the RIGHT (not becoming circle-8)
                                    left: '120%',
                                    top: '50%',
                                    width: circle8Size,
                                    height: circle8Size,
                                    x: '-50%',
                                    y: '-50%',
                                    opacity: 0
                                } : {
                                    // navigating right (shouldn't happen for home since it's index 0)
                                    left: '-20%',
                                    top: '50%',
                                    width: circle8Size,
                                    height: circle8Size,
                                    x: '-50%',
                                    y: '-50%',
                                    opacity: 0
                                }}
                                transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
                                style={{
                                    position: 'absolute',
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, ${prevColors.primary} 0%, ${prevColors.secondary} 100%)`,
                                    zIndex: 1
                                }}
                            />
                        );
                    })()}


                    {hasSeenHeroOnMount && previousPath && PAGE_ORDER[previousPath] !== undefined ? (() => {
                        const prevIndex = PAGE_ORDER[previousPath] ?? -1;
                        const currentIndex = PAGE_ORDER['/'];
                        const navigatingLeft = prevIndex > currentIndex;
                        const circle8Size = Math.min(windowSize.width, windowSize.height) * 0.082;

                        return (
                            <motion.div
                                className="circle"
                                initial={navigatingLeft ? {
                                    // coming from left side
                                    left: '-20%',
                                    top: '50%',
                                    width: circle8Size,
                                    height: circle8Size,
                                    x: '-50%',
                                    y: '-50%'
                                } : {
                                    // coming from circle-8 position
                                    left: `calc(98% - ${circle8Size}px)`,
                                    top: '86%',
                                    right: 'auto',
                                    width: circle8Size,
                                    height: circle8Size,
                                    x: '0%',
                                    y: '0%'
                                }}
                                animate={{ left: '50%', right: 'auto', top: '50%', width: targetSize, height: targetSize, x: '-50%', y: '-50%' }}
                                transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
                                style={{
                                    position: 'absolute',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #498CF6 0%, #236AD1 100%)',
                                    zIndex: 2
                                }}
                            />
                        );
                    })() : (
                        <animated.div className="circle circle1" style={circleSpring}></animated.div>
                    )}

                    {/* Global Red Circle (circle2) - Transitions from banner to circle-8 position */}
                    <animated.div
                        className="circle circle2"
                        style={{
                            ...circle2Spring,
                            borderRadius: '50%',
                            position: 'absolute',
                            zIndex: isHeroVisible ? 2 : 3,
                            animation: isHeroVisible ? 'none' : 'float2 15s infinite ease-in-out'
                        }}
                    />

                    {/* Banner Content (Text/Button) - Hidden on Mobile */}
                    {!isMobile && (
                        <animated.div style={{ ...bannerContentSpring, zIndex: 4, position: 'relative' }}>
                            <div className="banner-content">
                                <h1 className="banner-title">
                                    Building Good Things, <span className="color-text">Together!</span>
                                </h1>
                                <p>
                                    Google Developer Groups on Campus - USTP
                                </p>
                                <button
                                    className="banner-button"
                                    onClick={handleLearnMore}
                                >
                                    Learn More
                                </button>
                            </div>
                        </animated.div>
                    )}

                    <animated.div style={{ ...heroTitleSpring, position: 'absolute', zIndex: 10 }}>
                        <h1 className="home-hero-title">Home</h1>
                    </animated.div>
                </header>

                {/* Page content (revealed after clicking Learn More on desktop, or visible immediately on mobile) */}
                <animated.div
                    style={{
                        ...contentProps,
                        display: !showPageContent && !isMobile ? 'none' : 'block',
                        position: !showPageContent && !isMobile ? 'absolute' : 'relative',
                        visibility: !showPageContent && !isMobile ? 'hidden' : 'visible'
                    }}
                    ref={contentRef}
                >


                    <section className="py-24 px-4 bg-gray-50" data-aos="fade-up">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-[minmax(180px,auto)]">

                                {/* 1. Main Intro (Spans 2x2) */}
                                <div className="md:col-span-2 lg:row-span-2 bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-gray-100 relative overflow-hidden group flex flex-col justify-center">
                                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-60"></div>
                                    <div className="relative z-10">
                                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-bold mb-6">
                                            <SiGoogle className="text-xl" />
                                            <span>Who We Are</span>
                                        </div>
                                        <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 font-google-sans leading-tight">
                                            Fostering <span className="text-blue-600">Innovation</span>, <br className="hidden lg:block" /> Building Community
                                        </h2>
                                        <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl">
                                            This organization envisions itself as a community to be for men, women and for others, a community of developers that are passionate about uplifting communities and solving problems through science, technology, and innovation.
                                        </p>
                                        <Link to="/about-us">
                                            <button className="px-8 py-3 bg-gray-900 text-white rounded-full font-bold hover:bg-gray-800 transition-all hover:shadow-lg hover:-translate-y-1 flex items-center gap-2">
                                                More About Us <span className="text-xl">→</span>
                                            </button>
                                        </Link>
                                    </div>
                                </div>

                                {/* 2. Tall Image Tower (Spans 1x2 on LG) */}
                                <div className="md:col-span-1 lg:row-span-2 rounded-[2.5rem] overflow-hidden shadow-sm relative min-h-[400px] h-full group order-2 lg:order-none">
                                    <img
                                        src="/photos/community2.jpg"
                                        alt="Community"
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                                    <div className="absolute bottom-6 left-6 text-white">
                                        <p className="font-bold text-2xl mb-1 font-google-sans">Community First</p>
                                        <p className="text-sm opacity-90">Growing together</p>
                                    </div>
                                </div>

                                {/* 3. Small Stat Tile (Spans 1x1) */}
                                <div className="md:col-span-1 rounded-[2.5rem] bg-yellow-400 p-8 shadow-sm flex flex-col justify-center items-center text-center group hover:-translate-y-1 transition-transform duration-300">
                                    <p className="text-5xl font-bold text-gray-900 mb-2 font-google-sans">50+</p>
                                    <p className="text-sm font-bold uppercase tracking-wider text-gray-800">Events Hosted</p>
                                </div>

                                {/* 4. Social/Action Tile (Spans 1x1) */}
                                <a
                                    href="https://github.com/gdgoc-ustp/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="md:col-span-1 bg-gray-900 rounded-[2.5rem] p-8 flex flex-col justify-center items-center text-center shadow-lg group hover:bg-gray-800 transition-colors cursor-pointer decoration-none"
                                >
                                    <SiGithub className="text-5xl text-white mb-4 group-hover:scale-110 transition-transform" />
                                    <p className="text-white font-bold text-lg">Open Source</p>
                                    <p className="text-gray-400 text-sm">Contribute</p>
                                </a>

                                {/* 5. Mission Card (Spans 2x1 Wide) */}
                                <div className="md:col-span-2 bg-blue-600 rounded-[2.5rem] p-8 text-white shadow-xl relative overflow-hidden group flex items-center justify-start min-h-[200px]">
                                    <div className="absolute top-0 right-0 w-48 h-48 bg-white opacity-10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-125 transition-transform duration-700"></div>
                                    <div className="relative z-10 text-left pr-20 md:pr-24 w-full">
                                        <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-bold mb-4 uppercase tracking-wider">Our Mission</div>
                                        <h3 className="text-3xl font-bold font-google-sans leading-tight">Building Tomorrow's Developers</h3>
                                    </div>
                                    <div className="absolute right-6 md:right-8 z-10 flex-shrink-0">
                                        <Link to="/events" className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-white/20 hover:bg-white/30 rounded-full transition-colors">
                                            <span className="text-xl">→</span>
                                        </Link>
                                    </div>
                                </div>

                                {/* 6. Wide Image Tile (Spans 2x1) */}
                                <div className="md:col-span-2 rounded-[2.5rem] overflow-hidden shadow-sm relative h-[240px] group">
                                    <img
                                        src="/photos/hands-on.jpg"
                                        alt="Workshops"
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-blue-900/20 group-hover:bg-blue-900/10 transition-colors duration-300"></div>
                                    <div className="absolute bottom-6 left-6 text-white">
                                        <p className="font-bold text-2xl font-google-sans drop-shadow-lg">Hands-on Workshops</p>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </section>


                    <section className="py-24 bg-gray-50/50" data-aos="fade-up">
                        <div className="max-w-7xl mx-auto px-4">
                            <div className="flex flex-col items-center mb-16">
                                <span className="inline-block px-4 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                                    Our Network
                                </span>
                                <h1 className="text-4xl md:text-5xl font-bold text-center text-gray-900 font-google-sans leading-tight">
                                    Trusted by Organizations
                                </h1>
                            </div>

                            <div className="relative">
                                {/* Side masks for smoother fade */}
                                <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-gray-50/50 to-transparent z-10 pointer-events-none"></div>
                                <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-gray-50/50 to-transparent z-10 pointer-events-none"></div>

                                <div className="py-8 bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                                    <Marquee speed={35} pauseOnHover={true}>
                                        <div className="flex items-center gap-16 px-8">
                                            <div className="opacity-40 hover:opacity-100 transition-all duration-500 grayscale hover:grayscale-0 cursor-pointer">
                                                <img src="/partners/xu.jpg" alt="GDG - XU" className="h-12 w-auto object-contain" />
                                            </div>
                                            <div className="opacity-40 hover:opacity-100 transition-all duration-500 grayscale hover:grayscale-0 cursor-pointer">
                                                <img src="/partners/gdg-usls.jpg" alt="GDG - USLS" className="h-12 w-auto object-contain" />
                                            </div>
                                            <div className="opacity-40 hover:opacity-100 transition-all duration-500 grayscale hover:grayscale-0 cursor-pointer">
                                                <img src="/partners/DICT.png" alt="DICT Region X" className="h-14 w-auto object-contain" />
                                            </div>
                                            <div className="opacity-40 hover:opacity-100 transition-all duration-500 grayscale hover:grayscale-0 cursor-pointer flex items-center gap-3">
                                                <SiGoogle className="text-3xl" />
                                                <span className="font-bold text-xl text-gray-700">Google</span>
                                            </div>
                                            {/* Repeating for seamless loop if needed, though Marquee usually handles this */}
                                        </div>
                                    </Marquee>
                                </div>
                            </div>
                        </div>
                    </section>



                    <section className="py-24 px-4 bg-gray-50" data-aos="fade-up">
                        <div className="max-w-7xl mx-auto">
                            <h1 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-900 font-google-sans">What's Up?</h1>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
                                {newsLoading ? (
                                    <div className="col-span-1 md:col-span-2 lg:col-span-3 flex flex-col items-center justify-center p-12 bg-white rounded-3xl shadow-sm border border-gray-100 min-h-[400px]">
                                        <div className="loading-spinner mb-4 w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                                        <p className="text-xl text-gray-600 font-medium">Loading latest news...</p>
                                    </div>
                                ) : latestNews.length > 0 ? (
                                    latestNews.map((post) => (
                                        <div key={post.id} className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 group border border-gray-100 flex flex-col h-full transform hover:-translate-y-1">
                                            <div className="h-64 lg:h-80 overflow-hidden relative">
                                                <img
                                                    src={post.image_url
                                                        ? (post.image_url.startsWith('http')
                                                            ? post.image_url
                                                            : `https://yrvykwljzajfkraytbgr.supabase.co/storage/v1/object/public/blog-images/${post.image_url}`)
                                                        : About
                                                    }
                                                    alt={post.heading}
                                                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300"></div>
                                            </div>

                                            <div className="p-8 flex flex-col flex-grow relative">
                                                {/* Floating Date Badge */}
                                                <div className="absolute -top-5 right-8 bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg tracking-wide uppercase">
                                                    {(() => {
                                                        const date = new Date(post.created_at);
                                                        const now = new Date();
                                                        const diffTime = Math.abs(now - date);
                                                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                                                        if (diffDays < 7) {
                                                            return diffDays === 0 ? 'Today' : (diffDays === 1 ? 'Yesterday' : `${diffDays} days ago`);
                                                        }
                                                        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                                                    })()}
                                                </div>

                                                <div className="mb-4 mt-2">
                                                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 font-google-sans leading-tight group-hover:text-blue-600 transition-colors line-clamp-2">{post.heading}</h2>
                                                </div>

                                                <div className="text-gray-600 text-lg leading-relaxed mb-8 line-clamp-3">
                                                    {post.tagline || (() => {
                                                        const doc = new DOMParser().parseFromString(post.description, "text/html");
                                                        const text = doc.body.textContent || "";
                                                        return text;
                                                    })()}
                                                </div>

                                                <Link
                                                    to={`/news/article/${post.id}`}
                                                    className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-between text-blue-600 font-bold group-hover:translate-x-2 transition-transform duration-300 cursor-pointer text-lg"
                                                >
                                                    Read Article <span className="text-2xl transform transition-transform group-hover:translate-x-1">→</span>
                                                </Link>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-3xl p-16 text-center shadow-sm border border-gray-100">
                                        <div className="bg-gray-50 w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path></svg>
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900 mb-2 font-google-sans">No news available</h2>
                                        <p className="text-gray-500 text-lg">Check back soon for the latest updates from our community.</p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-16 flex justify-center">
                                <Link to="/news" className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold rounded-full text-lg border-2 border-blue-100 hover:border-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-300 shadow-sm hover:shadow-lg">
                                    Show More News
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                                </Link>
                            </div>
                        </div>
                    </section>


                    <section className="py-24 px-4 bg-white relative overflow-hidden" data-aos="fade-up">
                        {/* Geometric decorations */}
                        <div className="absolute top-0 left-0 w-64 h-64 bg-blue-100 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 opacity-60"></div>
                        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-50 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 opacity-60"></div>

                        <div className="max-w-7xl mx-auto relative z-10 text-center">
                            <h1 className="text-5xl md:text-7xl font-bold mb-8 text-gray-900 font-google-sans leading-tight">
                                Join our <span className="text-blue-600">Community</span>
                            </h1>

                            <div className="bg-white/80 backdrop-blur-sm rounded-[3rem] p-8 md:p-16 border border-gray-100 shadow-2xl relative">
                                {/* Mascot Image - Breaking the frame */}
                                <img
                                    src="/devy-whole.png"
                                    alt="Devy Mascot"
                                    className="absolute -bottom-12 -right-12 w-64 lg:w-80 z-20 pointer-events-none hidden lg:block drop-shadow-xl transform rotate-3"
                                />

                                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">
                                    Ready to build the future?
                                </h2>
                                <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed">
                                    Join our thriving community of developers to learn, share, and grow together.
                                    Connect with like-minded individuals and participate in events, workshops, and collaborative projects.
                                </p>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                                    <Link to="/register" className="w-full sm:w-auto">
                                        <button className="w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold rounded-full text-xl shadow-lg hover:shadow-blue-500/40 hover:-translate-y-1 transition-all duration-300">
                                            Join Now
                                        </button>
                                    </Link>
                                    <Link to="/events" className="w-full sm:w-auto">
                                        <button className="w-full sm:w-auto px-10 py-5 bg-white text-gray-800 font-bold rounded-full text-xl border-2 border-gray-200 hover:border-blue-500 hover:text-blue-600 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">
                                            View Events
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </section>
                </animated.div>
                {(showPageContent || isMobile) && <Footer />}
            </main>
        </div>
    );
}
